import * as errors from '@attain-apps/errors'
import { Multirange, Range } from '@attain-apps/range'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../../../models'
import * as expression from '../../../expression'
import { Value } from '../../../value'
import type { Query } from '../../query'
import { Relation } from '../../relation'
import type { Subquery } from '../../subquery'
import { Target } from '../../target'
import * as utils from '../utils'

import type { Context } from './context'
import { ForGroup } from './for-group'
import type { ForRecords } from './for-records'
import { ForSingleton } from './for-singleton'
import { ForValue } from './for-value'
import { Id } from './id'
import { Node } from './node'
import type { OfAnyType } from './of-any-type'

// Node.ForSingleton can only be produced by root queries, not subqueries
export function from<T>(
  query: Subquery<T>,
  context: Context,
): Exclude<OfAnyType<T>, ForSingleton<T>>
export function from<T>(query: Query<T>, context: Context): OfAnyType<T>
export function from<T>(query: Query<T>, context: Context): OfAnyType<T> {
  const { target } = query
  const {
    nodesCache,
    schema,
    parentInfo,
    subqueryNodes: preexistingSubqueryNodes = {},
  } = context
  const variables = {
    ...context.variables,
    ...query.variables,
  }
  const id = Id.create(context)

  const purpose: ForRecords['purpose'] =
    parentInfo && !query.purpose
      ? // If this query doesn't have a purpose but we have parent info, then
        // we use the parent purpose
        parentInfo.purpose
      : {
          include: PartialId.isPartialId(query.purpose?.include)
            ? [query.purpose?.include]
            : (query.purpose?.include ?? []),
          // TODO: put the default value somewhere better
          nil: query.purpose?.nil ?? 'last',
        }

  // This is mostly what we need when we use partialExpressionContext here, but
  // other parts like siblingExpressioNodes and subQueryNodes are filled in
  // later
  const partialExpressionContext: expression.node.Context = {
    ...context,
    queryNodeId: id,
    siblingExpressionNodes: {},
    subqueryNodes: {},
    variables,
  }

  // For variables in creation of the query graph (so wherever we pass
  // variables), we need to check if they are an instance.
  // If they are, then we render them into a value and if not we do nothing.
  for (const key in variables) {
    const value = variables[key]

    // FIRST: Try to reconstitute from plain JSON envelope values (e.g.
    // { $range: ... }, { $multirange: ... }). This must come before
    // Instance.isInstance because fromPlain handles ISO-string endpoints
    // (e.g. { $range: { start: "2025-01-01T..." } }) by converting them
    // to DmsDate, whereas Instance.isInstance + createNode would treat
    // them as string primitives, producing Range<string> instead of
    // Range<Date>.
    const reconstituted = Value.fromPlainValue(value)
    if (reconstituted !== undefined && reconstituted !== value) {
      variables[key] = reconstituted
      continue
    }

    // Check if variable is an instance expression and evaluate it
    if (expression.instance.Instance.isInstance(value)) {
      // NOTE: Typescript tries to infer the type parameter for our expression
      // (i.e., value). Because our expression can be any instance, typescript
      // can't infer anything better than unknown. However, due to the
      // complexity of the types, an instance expression doesn't match
      // Expression<unknown>. So, we pass a type parameter of `any` to tell
      // typescript that we are okay with any valid expression.
      const valueNode = expression.node.from<any>(
        value,
        partialExpressionContext,
      )
      expression.node.ForValue.assertIsForValue(
        valueNode,
        `Variable "${key}" must be a static instance`,
      )
      variables[key] = valueNode.value
    }
  }

  // The value of the period remains the same regardless of whether we are
  // dealing with an entity target or a dimension target.
  let period: ForRecords['period'] = query.period
    ? expression.node.from(query.period, partialExpressionContext)
    : parentInfo?.period

  if (
    ForValue.isForValue(period) &&
    isDateRangeOrMultirange(period.value) &&
    period.value.includesEverything
  ) {
    // In the case that period is a value node that includes everything, we set the period to undefined because it is not needed anymore
    period = undefined
  }

  const currentQueryInfo: Omit<Context.ParentInfo, 'entity'> = {
    id,
    period,
    purpose,
  }

  if (!target) {
    // If there is not target, this is an aggregate query
    if (parentInfo) {
      // Aggregate queries can only exist at the top level. Subqueries
      // cannot be aggregate queries
      throw new errors.ValidationError({
        reason:
          'Subqueries must have a target. Only the root query can omit the target',
        type: `DMS subquery${context.name ? `(${context.name})` : ''}`,
      })
    }
    // NOTE: we don't need to bind these subqueries since this is an
    // aggregation node, not an entity node
    const { nodes: subqueryNodes } = utils.createSubqueryNodes(
      query.subqueries,
      {
        nodesCache,
        parentInfo: currentQueryInfo,
        schema,
        variables,
      },
    )
    const expressionContext: expression.node.Context = {
      ...partialExpressionContext,
      subqueryNodes: {
        ...preexistingSubqueryNodes,
        ...subqueryNodes,
      },
    }
    const selection = expression.node.from(
      query.selection ?? utils.getDefaultSelection(query),
      expressionContext,
    )
    if (expression.node.ForValue.isForValue(selection)) {
      // If the selection can be reduced to a concrete value, we just return that value
      return new ForValue<T>({
        id,
        nodesCache,
        query,
        // NOTE: we just hope this value matches the expected type. If not,
        // that is on whoever wrote this DMS query
        value: selection.value as T,
      })
    }
    return new ForSingleton({
      id,
      nodesCache,
      query,
      schema,
      selection,
      subqueryNodes,
    })
  }

  /**
   * Returns `context.parentInfo.modelId`, but asserts that it is a
   * {@link models.Entity.Id}
   */
  function getParentEntityId() {
    return TypeAsserter.validate(
      context.parentInfo?.source?.modelId,
      models.Entity.Id.assertIsId,
    )
  }
  if (Target.DateRange.List.isList(target)) {
    const subqueryNodes = utils.createSubqueryNodes(query.subqueries, {
      nodesCache,
      parentInfo: {
        ...currentQueryInfo,
      },
      schema,
      variables,
    })

    const expressionContext: expression.node.Context = {
      ...context,
      listOfDateRanges: target,
      queryNodeId: id,
      siblingExpressionNodes: {},
      subqueryNodes: subqueryNodes.nodes,
      variables,
    }
    return utils.createMemberNode({
      expressionContext,
      id,
      nodesCache,
      period,
      purpose,
      query,
      schema,
      subqueryNodes,
    })
  } else if (
    Relation.Where.Singleton.isSingleton(target) ||
    models.ForRecord.Id.isId(target)
  ) {
    // Records node

    let modelId: models.ForRecord.Id
    if (Relation.isRelation(target)) {
      const relative = schema.tryGetRelative(getParentEntityId(), target)
      if (!relative) {
        // If the relative doesn't exist, then we just return an empty list
        return new ForValue<Maybe.ReadonlyArray<T>>({
          id,
          nodesCache,
          query,
          // Singleton = undefined | Otherwise = empty list
          value:
            query.quantity === 1 && !query.noUnpacking ? (undefined as T) : [],
          // TODO: use existing utility function to determine if singleton
        })
      }
      modelId = relative.id
    } else {
      modelId = target
    }

    const subqueryNodes = utils.createSubqueryNodes(query.subqueries, {
      nodesCache,
      parentInfo: {
        ...currentQueryInfo,
        source: { modelId },
      },
      schema,
      variables,
    })

    const expressionContext: expression.node.Context = {
      ...partialExpressionContext,
      modelId,
      subqueryNodes: { ...preexistingSubqueryNodes, ...subqueryNodes.nodes },
    }

    // NOTE: this is not actually a member of a group, but the function
    // still does what we need
    return utils.createMemberNode({
      expressionContext,
      id,
      nodesCache,
      period,
      purpose,
      query,
      schema,
      subqueryNodes,
    })
  } else {
    // Group node

    /**
     * Entities for which we will create {@link Node.ForGroup.Member}
     */
    let entities: readonly models.Entity[]
    switch (target) {
      case 'ancestors':
        entities = schema.getAncestorsOf(getParentEntityId())
        break
      case 'descendants':
        entities = schema.getDescendantsOf(getParentEntityId())
        break
      default:
        entities = schema.getEntitiesInDimension(target)
        break
    }

    /**
     * @see {@link Node.ForGroup.members}
     */
    const members = entities
      .map(entity => {
        const entityNodeId = [id, entity.id].join('/')
        const subqueryNodes = utils.createSubqueryNodes(query.subqueries, {
          nodesCache,
          parentInfo: {
            ...currentQueryInfo,
            id: entityNodeId,
            source: { modelId: entity.id },
          },
          schema,
          variables,
        })
        // Create a member node for each entity in the dimension
        return utils.createMemberNode<T>({
          expressionContext: {
            ...context,
            modelId: entity.id,
            siblingExpressionNodes: {},
            subqueryNodes: {
              ...preexistingSubqueryNodes,
              ...subqueryNodes.nodes,
            },
            variables,
          },
          id: entityNodeId,
          nodesCache,
          period,
          purpose,
          query: {
            ...query,
            target: entity.id,
          },
          schema,
          subqueryNodes,
        })
      })
      // Filter out entities that doesn't have any results
      .filter(
        member =>
          !(
            ForValue.isForValue(member) &&
            TypeChecker.isReadonlyArray(member.value) &&
            member.value.length === 0
          ),
      )
    const valueMembers = members.filter(
      (member): member is Extract<typeof member, ForValue<T>> =>
        ForValue.isForValue(member),
    )
    if (valueMembers.length === members.length) {
      const values = valueMembers.flatMap(member => member.value)
      return new ForValue({
        id,
        nodesCache,
        query,
        // We have to unpack if the results are meant to be a singleton
        value: query.quantity === 1 && !query.noUnpacking ? values[0] : values,
        // TODO: use existing utility function to determine if singleton
      })
    }

    return new ForGroup<T>({
      id,
      members,
      nodesCache,
      query,
      schema,
    })
  }
}

// TODO: This exact same typechecker exists in query.ts. We should make this
// into a reusable utility function. Possibly, create separate
// range-or-multirange.ts file with its own RangeOrMultiRange type with helpers
// including typechecker, toRange, and toMultirange.
const isDateRangeOrMultirange = TypeChecker.Factory.union(
  Range.getTypeChecker(TypeChecker.isDate),
  Multirange.getTypeChecker(TypeChecker.isDate),
)
